import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Truck, AlertTriangle, X, RefreshCw, Wrench } from "lucide-react";
import API from "../services/api";
import { errMsg } from "./runsheets/util";
import {
  EXPIRY_FIELDS, ExpiryChip, StatusChip, FaultRow, shortDate, sortNewest, isActiveTruck,
} from "./trucks/util";
import TruckHistory from "./trucks/TruckHistory";

/* The fleet as the office needs to see it: every truck, what is about to
 * expire on it, when it was last checked and by whom, and which trucks are
 * carrying a fault nobody has actioned yet.
 *
 * This is the digital form of NZTA's "Daily Checklist - Goods Service
 * Vehicles". Drivers fill it in on the app before every shift; the office
 * reads it here, records the "Action Taken" line, and prints the range back
 * out as the paper form when an inspector asks for it. Nothing on this page
 * deletes a record - a retired truck keeps its history, and "Remove" on a
 * check voids it (hidden everywhere, kept on file with who and why) rather
 * than deleting it.
 *
 * Admin/IT only, by the tab gate in App.js.
 */
export default function Trucks() {
  const [trucks, setTrucks] = useState([]);
  const [openFaults, setOpenFaults] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showRetired, setShowRetired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // Which open fault in the fleet panel has a form under it, and which form -
  // one at a time, so an action and a removal cannot be half-filled together.
  const [formFor, setFormFor] = useState(null);   // { id, kind: "action" | "void" }
  const formOf = (id) => (formFor?.id === id ? formFor.kind : null);
  const toggleForm = (id, kind) => setFormFor(formOf(id) === kind ? null : { id, kind });

  const reportError = useCallback((err) => setError(errMsg(err)), []);
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  // Two lists, reported on separately, so one failing request leaves the
  // other on screen with a banner saying why - never a silently empty page.
  const loadTrucks = useCallback(async () => {
    try {
      const res = await API.get("/trucks");
      setTrucks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      reportError(err);
    }
  }, [reportError]);

  // No date bounds on purpose: an open fault stays open until the office
  // records what was done, however long ago the driver found it.
  const loadOpenFaults = useCallback(async () => {
    try {
      const res = await API.get("/checks?status=fault");
      // The API leaves removed checks out; the filter is the belt to that
      // brace so a removed test fault never turns a truck red.
      setOpenFaults(sortNewest(res.data?.checks).filter((c) => !c.voided));
    } catch (err) {
      reportError(err);
    }
  }, [reportError]);

  const refresh = useCallback(async () => {
    await Promise.all([loadTrucks(), loadOpenFaults()]);
  }, [loadTrucks, loadOpenFaults]);

  useEffect(() => {
    let cancelled = false;
    refresh().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refresh]);

  const actionDone = async () => {
    setFormFor(null);
    setNotice("Action recorded. The check keeps its fault; the truck no longer shows it as open.");
    await refresh();
  };

  // Removing a check changes the truck's last-check and open-fault figures,
  // so the whole fleet reloads, not just this panel.
  const voidDone = async () => {
    setFormFor(null);
    setNotice("Check removed. It stays on record, marked removed by you with your reason - open the truck and tick \"Show removed\" to see it.");
    await refresh();
  };

  // Faults first, then by plate, so the truck that needs attention is at the
  // top. Retired trucks stay behind a toggle - their records are kept, they
  // just are not part of today's fleet.
  const fleet = useMemo(() => {
    const rows = trucks.filter((t) => showRetired || isActiveTruck(t));
    return rows.sort((a, b) =>
      (Number(b.open_faults || 0) - Number(a.open_faults || 0))
      || String(a.license_plate || a.id).localeCompare(String(b.license_plate || b.id)));
  }, [trucks, showRetired]);

  const retiredCount = trucks.length - trucks.filter(isActiveTruck).length;
  const selected = selectedId ? trucks.find((t) => t.id === selectedId) : null;

  const expiryCounts = useMemo(() => {
    let expired = 0;
    let due = 0;
    trucks.filter(isActiveTruck).forEach((t) => {
      (t.expiry_warnings || []).forEach((w) => { if (typeof w.days === "number" && w.days < 0) expired += 1; else due += 1; });
    });
    return { expired, due };
  }, [trucks]);

  if (loading) return <div className="p-8 text-center text-slate-400">Loading trucks…</div>;

  return (
    <div className="max-w-[1500px] mx-auto space-y-5">
      {/* Header */}
      <div className="border-b border-slate-800 pb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-white tracking-tight flex items-center gap-2">
            <Truck size={22} className="text-amber-400" /> Trucks
          </h2>
          <p className="text-slate-400 text-xs mt-1">
            Daily pre-start checks by truck, whoever drove it. A fault turns the truck red until the office records the action taken.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* The same Fleet section the 7am digest carries - expired and
              expiring documents, service due, open faults - emailed now to
              the digest mailbox (btdavtransport@gmail.com). */}
          <button
            onClick={async () => {
              setError(null);
              try {
                const res = await API.post("/trucks/alerts", { send: true });
                if (res.data?.sent) {
                  setNotice(`Fleet alerts emailed to ${res.data.to}${res.data.alerts?.quiet ? " - nothing due, no open faults." : "."}`);
                } else {
                  setError(`Could not email the alerts: ${res.data?.error || "unknown error"}`);
                }
              } catch (err) {
                setError(err.response?.data?.detail || err.message);
              }
            }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-amber-500/10 border border-amber-500/30 text-amber-200 hover:border-amber-400 transition-all"
            title="Email expiring documents, service due and open faults to the office mailbox now"
          >
            <AlertTriangle size={13} /> Email fleet alerts
          </button>
          <button onClick={refresh}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-slate-900/60 border border-slate-800 text-slate-200 hover:border-slate-600 transition-all">
            <RefreshCw size={13} /> Reload
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-300 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span className="flex-1 break-words">{error}</span>
          <button onClick={() => setError(null)} className="text-red-300/70 hover:text-white"><X size={14} /></button>
        </div>
      )}
      {notice && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-sm text-emerald-300 flex items-center gap-2">
          <span className="flex-1">{notice}</span>
          <button onClick={() => setNotice(null)} className="text-emerald-300/70 hover:text-white"><X size={14} /></button>
        </div>
      )}

      {selected ? (
        <TruckHistory truck={selected} onBack={() => setSelectedId(null)} onChanged={refresh}
          reportError={reportError} notify={setNotice} />
      ) : (
        <>
          {/* Totals at a glance */}
          <div className="flex flex-wrap items-center gap-5 text-xs bg-slate-950/40 rounded-2xl border border-slate-800/80 p-4">
            <div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Trucks</div>
              <div className="text-lg font-black text-white font-mono">{trucks.filter(isActiveTruck).length}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Open faults</div>
              <div className={`text-lg font-black font-mono ${openFaults.length ? "text-red-400" : "text-slate-500"}`}>{openFaults.length}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Expired</div>
              <div className={`text-lg font-black font-mono ${expiryCounts.expired ? "text-red-400" : "text-slate-500"}`}>{expiryCounts.expired}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Due in 30 days</div>
              <div className={`text-lg font-black font-mono ${expiryCounts.due ? "text-amber-400" : "text-slate-500"}`}>{expiryCounts.due}</div>
            </div>
            {retiredCount > 0 && (
              <label className="ml-auto flex items-center gap-2 text-slate-400 cursor-pointer">
                <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
                show {retiredCount} retired
              </label>
            )}
          </div>

          {/* Every uncleared fault, fleet-wide, so nobody has to open each truck */}
          <div className={`bg-slate-950/40 rounded-2xl border p-4 space-y-2 ${openFaults.length ? "border-red-500/30" : "border-slate-800/80"}`}>
            <h3 className={`font-bold text-sm border-b border-slate-800 pb-2 flex items-center gap-2 ${openFaults.length ? "text-red-200" : "text-white"}`}>
              <Wrench size={15} /> Open faults ({openFaults.length})
            </h3>
            {openFaults.length === 0 ? (
              <p className="text-xs text-slate-500 py-3 text-center">No open faults. Every reported fault has an action recorded against it.</p>
            ) : openFaults.map((c) => (
              <FaultRow key={c.id} check={c} showTruck onOpenTruck={(id) => setSelectedId(id)}
                form={formOf(c.id)}
                onToggleForm={(kind) => toggleForm(c.id, kind)}
                onDone={actionDone} onVoided={voidDone} onCancel={() => setFormFor(null)} reportError={reportError} />
            ))}
          </div>

          {/* The fleet */}
          <div className="bg-slate-950/40 rounded-2xl border border-slate-800/80 p-4 space-y-2">
            <h3 className="font-bold text-white text-sm border-b border-slate-800 pb-2">
              Fleet ({fleet.length})
              <span className="text-slate-500 font-normal"> · click a truck for its checks and the printable form</span>
            </h3>
            {fleet.length === 0 ? (
              <p className="text-xs text-slate-500 py-4 text-center bg-slate-900/10 rounded-xl border border-dashed border-slate-800">
                No trucks registered. Add them in Settings → Active Truck Assets.
              </p>
            ) : fleet.map((t) => {
              const faults = Number(t.open_faults || 0);
              const last = t.last_check || null;
              return (
                <button key={t.id} onClick={() => setSelectedId(t.id)}
                  className={`w-full text-left p-3 rounded-xl border-l-4 border text-xs transition-all hover:brightness-125 ${
                    faults
                      ? "bg-red-500/5 border-red-500/30 border-l-red-500"
                      : "bg-slate-900/40 border-slate-800/80 border-l-amber-500"} ${isActiveTruck(t) ? "" : "opacity-60"}`}>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <span className="font-extrabold text-slate-100 text-sm">{t.license_plate || t.id}</span>
                    {t.fleet_no && <span className="text-slate-400">Fleet <span className="text-slate-200 font-medium">{t.fleet_no}</span></span>}
                    {t.make && <span className="text-slate-400">{t.make}</span>}
                    {!isActiveTruck(t) && (
                      <span className="px-2 py-0.5 rounded border text-[10px] font-bold text-slate-400 border-slate-700 bg-slate-800/60">retired</span>
                    )}
                    <span className="flex flex-wrap gap-1.5">
                      {EXPIRY_FIELDS.map((f) => <ExpiryChip key={f.key} truck={t} field={f.key} label={f.label} />)}
                    </span>
                    <span className="ml-auto flex flex-wrap items-center gap-2">
                      {/* Last check: who took it out and how it came back */}
                      {last ? (
                        <span className="text-slate-400 whitespace-nowrap">
                          Last check <span className="text-slate-200 font-medium">{shortDate(last.date)}</span>
                          {last.person_name ? <> · <span className="text-slate-200">{last.person_name}</span></> : null}
                          {" "}<StatusChip status={last.status} />
                        </span>
                      ) : (
                        <span className="text-slate-500 whitespace-nowrap">No check recorded yet</span>
                      )}
                      {faults > 0 && (
                        <span className="px-2 py-0.5 rounded border text-[10px] font-bold text-red-200 border-red-500/50 bg-red-500/15 whitespace-nowrap">
                          {faults} open fault{faults === 1 ? "" : "s"}
                        </span>
                      )}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* The line that has to be on the product, not just in the code. */}
      <p className="text-[10px] text-slate-500 text-center pb-2">
        Daily checklist - Goods Service Vehicles. Records are kept and never deleted; a removed check stays on file,
        marked with who removed it and why.
      </p>
    </div>
  );
}
